import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  InputAdornment,
  TextField,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  FileCopy as FileCopyIcon,
  Check as CheckCircleIcon,
  Home,
  Close as CloseIcon,
  AttachFile as AttachFileIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { fetchQuotes } from '../../redux/slices/quoteSlice';
import { QuotesTable } from '../../components/DataTable/examples';
import { QuoteData } from '../../components/DataTable/examples/QuotesTable';
import { Quote } from '../../types';
import BackButton from '../../components/BackButton/BackButton';
import styles from './QuotesPage.module.css';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import * as quoteService from '../../services/quote-service';
import { useSnackbar } from 'notistack';
import { debounce } from 'lodash'; // Import debounce for better performance

const QuotesPage: React.FC = () => {
  const [filter, setFilter] = useState<string>('Filtre por...');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedQuoteId, setSelectedQuoteId] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { setBreadcrumbs } = useBreadcrumb();
  const { enqueueSnackbar } = useSnackbar();

  const { items: quotes, loading, error } = useAppSelector(state => state.quotes);

  useEffect(() => {
    dispatch(fetchQuotes(searchTerm));
  }, [dispatch, setBreadcrumbs, searchTerm]);

  // Debounced search function to avoid too many API calls
  const debouncedSearch = debounce((term: string) => {
    if (term.trim() === '') {
      dispatch(fetchQuotes(''));
    } else {
      dispatch(fetchQuotes(term));
    }
  }, 500);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value;
    setSearchTerm(term);
    // No need for debounce since we're using backend filtering
    dispatch(fetchQuotes(term));
  };

  const handleViewQuote = (quoteId: string) => {
    navigate(`/quotes/${quoteId}`);
  };

  const handleEditQuote = (quoteId: string) => {
    navigate(`/quotes/edit/${quoteId}`);
  };

  const handleConvertQuote = async (quoteId: string) => {
    setSelectedQuoteId(quoteId);
    setIsUploadModalOpen(true);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUploadConfirm = async () => {
    try {
      setIsUploading(true);

      const formData = new FormData();
      if (selectedFile) {
        formData.append('file', selectedFile);
      }

      const response = await quoteService.uploadQuoteConversion(selectedQuoteId, formData);

      if (response.success) {
        enqueueSnackbar('Solicitação de conversão de cotação para pedido em analise', {
          variant: 'success',
          autoHideDuration: 5000,
        });

        // Refresh quotes list
        dispatch(fetchQuotes(searchTerm));
      } else {
        enqueueSnackbar('Houve um erro ao solicitar conversão de cotação em pedido.', {
          variant: 'error',
          autoHideDuration: 5000,
        });
      }
    } catch (error) {
      console.error('Error uploading quote conversion:', error);
      enqueueSnackbar('Houve um erro ao solicitar conversão de cotação em pedido.', {
        variant: 'error',
        autoHideDuration: 5000,
      });
    } finally {
      setIsUploading(false);
      setIsUploadModalOpen(false);
      setSelectedFile(null);
      setSelectedQuoteId('');
    }
  };

  const handleUploadCancel = () => {
    setIsUploadModalOpen(false);
    setSelectedFile(null);
    setSelectedQuoteId('');
  };

  // Helper function to calculate total - MOVED UP before it's used
  const calculateTotal = (quote: Quote): string => {
    // First check if quote_items is available (as shown in the API response example)
    if (quote.quote_items && quote.quote_items.length > 0) {
      const total = quote.quote_items.reduce((sum, item) => {
        return sum + item.quantity * item.unit_price;
      }, 0);

      // Format as Brazilian currency
      return total.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }

    // Fall back to using items if quote_items is not available
    if (!quote.items || quote.items.length === 0) {
      return '0,00';
    }

    const total = quote.items.reduce((sum, item) => {
      return sum + item.quantity * item.unit_price;
    }, 0);

    // Format as Brazilian currency
    return total.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Transform quote data
  const quoteTableData: QuoteData[] = quotes.map(quote => ({
    id: quote.id,
    client: quote.customer?.company_name || 'Cliente não identificado', // Use company_name from customer object
    number: quote.code || quote.id, // Use the formatted code or fall back to ID if not available
    date: new Date(quote.expiration_date).toLocaleDateString('pt-BR'), // Format date
    status: quote.status,
    total: 'R$ ' + calculateTotal(quote), // Calculate total if available
  }));

  return (
    <>
      {/* Back button */}
      <BackButton />

      {/* Header section */}
      <Box
        sx={{
          mb: 3,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              width: { xs: '100%', md: '60%' },
            }}
          >
            <Typography variant="h5">
              Cotações: gerencie e agilize suas compras com eficiência.
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              Aqui você encontra todas as suas cotações, podendo revisá-las, editá-las ou
              transformá-las em pedidos com poucos cliques. Use os filtros para localizar
              rapidamente suas negociações e mantenha o controle total sobre suas compras.
            </Typography>
          </Box>
          <Box
            sx={{
              width: { xs: '100%', md: '40%' },
              display: 'flex',
              alignItems: 'center',
              justifyContent: { xs: 'flex-start', md: 'flex-end' },
              mt: { xs: 1, md: 0 },
            }}
          >
            <Button
              component={Link}
              to="/quotes/new"
              variant="contained"
              sx={{
                bgcolor: 'var(--acer-dark)',
                '&:hover': { bgcolor: '#333' },
                textTransform: 'none',
                borderRadius: '1px',
                color: 'white',
                height: '40px',
                maxHeight: '40px',
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              Criar nova cotação
            </Button>
          </Box>
        </Box>
        {/* Tip box */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            bgcolor: '#fafff6',
            p: 2,
            borderRadius: 1,
            flexDirection: { xs: 'column', sm: 'row' },
            mt: 2,
          }}
        >
          <CheckCircleIcon
            sx={{
              color: '#2E7D32',
              fontSize: 16,
              border: '1px solid #2E7D32',
              borderRadius: '50%',
              mt: { xs: 0.5, sm: 0.5 },
            }}
          />
          <Typography variant="body2" sx={{ color: '#2E7D32' }}>
            <span style={{ fontWeight: 500 }}>Dica:</span> Quer refazer um pedido já cotado? Basta
            localizar sua cotação e convertê-la em compra em segundos.
          </Typography>
        </Box>
      </Box>

      {/* Search and action section */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          mb: 3,
        }}
      >
        <TextField
          placeholder="Pesquise por número da cotação, cliente ou status"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={handleSearch}
          sx={{
            flexGrow: 1,
            maxWidth: '500px',
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'white',
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* DataTable section */}
      <Paper sx={{ borderRadius: 1, overflow: 'hidden', p: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <img
            src="/icons/quote.svg"
            alt="Quote icon"
            style={{
              width: 24,
              height: 24,
              marginRight: '8px',
            }}
          />
          <Typography variant="h6" sx={{ fontWeight: 500, color: '#2b3445', fontSize: '18px' }}>
            Minhas cotações
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography>Carregando cotações...</Typography>
          </Box>
        ) : error ? (
          <Box sx={{ p: 3, textAlign: 'center', color: 'error.main' }}>
            <Typography>Erro ao carregar cotações: {error}</Typography>
          </Box>
        ) : quotes.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography>Nenhuma cotação encontrada. Crie sua primeira cotação agora!</Typography>
            <Button
              component={Link}
              to="/quotes/new"
              variant="contained"
              sx={{
                mt: 2,
                bgcolor: 'var(--acer-dark)',
                '&:hover': { bgcolor: '#333' },
                textTransform: 'none',
                borderRadius: '1px',
                color: 'white',
              }}
            >
              Criar nova cotação
            </Button>
          </Box>
        ) : (
          <QuotesTable
            data={quoteTableData}
            onViewQuote={handleViewQuote}
            onEditQuote={handleEditQuote}
            onConvertQuote={handleConvertQuote}
          />
        )}
      </Paper>

      {/* Upload Modal */}
      <Dialog open={isUploadModalOpen} onClose={handleUploadCancel} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Typography variant="h6">Converter cotação em pedido</Typography>
          <IconButton onClick={handleUploadCancel} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            Anexe um arquivo ou envie um e-mail para <strong>acer@cisp1.io</strong>
          </Alert>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <input
              accept="*/*"
              style={{ display: 'none' }}
              id="file-upload"
              type="file"
              onChange={handleFileChange}
            />
            <label htmlFor="file-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<AttachFileIcon />}
                fullWidth
                sx={{
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  py: 1.5,
                }}
              >
                {selectedFile ? selectedFile.name : 'Anexar arquivo (opcional)'}
              </Button>
            </label>

            {selectedFile && (
              <Typography variant="body2" color="text.secondary">
                Arquivo selecionado: {selectedFile.name}
              </Typography>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={handleUploadCancel} variant="outlined" disabled={isUploading}>
            Cancelar
          </Button>
          <Button
            onClick={handleUploadConfirm}
            variant="contained"
            disabled={isUploading}
            sx={{
              bgcolor: '#80c343',
              color: '#fff',
              '&:hover': { bgcolor: '#5b8012' },
            }}
          >
            {isUploading ? 'Enviando...' : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default QuotesPage;
